import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import api from '../../utils/api';
import StyledSelect from '../StyledSelect';

export default function BankingStep({ client, onComplete }) {
  const [linkToken, setLinkToken] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  
  const ach = client?.company?.ach;
  // A bank is "connected" only if there's actual saved content (Plaid item, masked
  // account, or manually-entered routing/account). An empty {} placeholder \u2014 which
  // some seed/migration paths create \u2014 must NOT trigger the "connected" view.
  const hasAch = !!(
    ach && (
      ach.itemId ||
      ach.plaidAccessToken ||
      ach.accountMasked ||
      ach.routingMasked ||
      (ach.routing && ach.account) ||
      (ach.bankName && (ach.account || ach.accountNumber))
    )
  );

  // Fetch link_token from backend
  const fetchLinkToken = useCallback(async () => {
    if (hasAch) return; // Don't fetch if already connected
    
    setLoadingLink(true);
    try {
      const response = await api.post('/api/plaid/create-link-token');
      setLinkToken(response.link_token);
    } catch (err) {
      console.error('Failed to create link token:', err);
      setError('Failed to initialize Plaid. Please try again or use manual entry.');
    } finally {
      setLoadingLink(false);
    }
  }, [hasAch]);

  useEffect(() => {
    fetchLinkToken();
  }, [fetchLinkToken]);

  // Handle successful Plaid Link connection
  const onSuccess = useCallback(async (public_token, metadata) => {
    setSaving(true);
    setError(null);
    
    try {
      // Exchange public_token for access_token
      const exchangeResponse = await api.post('/api/plaid/exchange-public-token', {
        public_token
      });
      
      // Save bank connection with access_token
      await api.clients.connectBankPlaid(client.id, {
        accountId: metadata.account_id,
        metadata: {
          ...metadata,
          access_token: exchangeResponse.access_token,
          item_id: exchangeResponse.item_id
        }
      });
      
      // Call onComplete to refresh parent
      if (onComplete) {
        onComplete();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to connect bank:', err);
      setError('Failed to connect bank account. Please try again.');
      setSaving(false);
    }
  }, [client.id, onComplete]);

  const onExit = useCallback((err, metadata) => {
    if (err) {
      console.error('Plaid Link error:', err);
      setError('Failed to connect bank account.');
    }
  }, []);

  // Initialize Plaid Link
  const config = {
    token: linkToken,
    onSuccess,
    onExit,
  };

  const { open, ready } = usePlaidLink(config);

  const handlePlaidConnect = () => {
    if (ready) {
      open();
    }
  };
  
  if (hasAch) {
    return <BankingConnected ach={ach} onReconnect={handlePlaidConnect} onComplete={onComplete} />;
  }
  
  if (showManualForm) {
    return <ManualACHForm 
      client={client} 
      onBack={() => setShowManualForm(false)}
      onComplete={onComplete}
    />;
  }
  
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        STEP 2: Set Up Payment
      </h2>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
        We need your banking details to process plan payments. The fastest way is to connect your bank securely through Plaid.
      </p>
      
      {/* Contact Info */}
      <div style={{ 
        background: C.bg, 
        border: `1px solid ${C.border}`, 
        borderRadius: 8, 
        padding: 16,
        marginBottom: 24 
      }}>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8 }}>
          Your contact for this step:
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          Cassie Nix · Director of Operations/Payroll
        </div>
        <div style={{ fontSize: 13, color: C.text }}>
          c.nix@healthcues.com · 678.522.9988
        </div>
      </div>
      
      {error && (
        <div style={{
          background: C.redLight,
          border: `1px solid ${C.red}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <Icon name="alert-circle" style={{ fontSize: 20, color: C.red }} />
          <span style={{ fontSize: 13, color: C.red }}>{error}</span>
        </div>
      )}
      
      {/* Main connection options */}
      <div style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Icon name="business-outline" style={{ fontSize: 48, color: C.blue, marginBottom: 16 }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            Connect Your Bank Account
          </h3>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
            Securely connect your bank through Plaid. We'll verify your account instantly — no manual entry needed.
          </p>
          
          {/* Security badges */}
          <div style={{ 
            display: 'flex', 
            gap: 16, 
            justifyContent: 'center', 
            marginBottom: 24,
            fontSize: 12,
            color: C.textMid 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="lock-closed" style={{ fontSize: 14, color: C.green }} />
              Bank-level encryption
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="eye-off-outline" style={{ fontSize: 14, color: C.green }} />
              Read-only access
            </div>
          </div>
          
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 24, lineHeight: 1.5 }}>
            We can only verify your account — we cannot move money without your authorization.
          </div>
          
          {/* Plaid button */}
          <button
            onClick={handlePlaidConnect}
            disabled={saving || loadingLink || !ready}
            style={{
              padding: '14px 32px',
              background: (saving || loadingLink || !ready) ? C.textLight : C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: (saving || loadingLink || !ready) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="business" style={{ fontSize: 18 }} />
            {saving ? 'Connecting...' : loadingLink || !ready ? 'Loading...' : 'Connect with Plaid'}
          </button>
        </div>
        
        {/* Divider */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16, 
          margin: '32px 0',
          color: C.textLight,
          fontSize: 13
        }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
        
        {/* Manual entry button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => setShowManualForm(true)}
            style={{
              padding: '10px 24px',
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Enter bank details manually
          </button>
        </div>
      </div>
    </div>
  );
}

function BankingConnected({ ach, onReconnect, onComplete }) {
  const isVerified = ach.verificationStatus === 'verified';
  const isPending = ach.verificationStatus === 'pending';
  
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        STEP 2: Set Up Payment
      </h2>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
        {isVerified ? 'Your bank account has been connected.' : 'Your bank account is pending verification.'}
      </p>
      
      <div style={{
        background: isVerified ? C.greenLight : C.orangeLight,
        border: `1px solid ${isVerified ? C.green : C.orange}`,
        borderRadius: 12,
        padding: 32
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: 16, marginBottom: 24 }}>
          <Icon 
            name={isVerified ? "checkmark-circle" : "time-outline"} 
            style={{ fontSize: 48, color: isVerified ? C.green : C.orange }} 
          />
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              {isVerified ? 'Bank Account Connected' : 'Bank Account Saved — Pending Verification'}
            </h3>
            {isPending && (
              <p style={{ fontSize: 13, color: C.textMid, marginBottom: 16 }}>
                We'll send two small deposits (under $1.00) to your account within 1-2 business days. You'll enter the amounts here to verify.
              </p>
            )}
          </div>
        </div>
        
        {/* Account details */}
        <div style={{ 
          background: 'white', 
          borderRadius: 8, 
          padding: 20,
          marginBottom: 24 
        }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Bank:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{ach.bankName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Account:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {ach.accountType === 'checking' ? 'Business Checking' : 'Business Savings'} {ach.accountMasked}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: C.textMid }}>Routing:</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{ach.routingMasked}</span>
            </div>
            {isVerified && ach.verifiedAt && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: C.textMid }}>Verified:</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {new Date(ach.verifiedAt).toLocaleDateString()} via {ach.verifiedVia === 'plaid' ? 'Plaid' : 'Micro-deposit'}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={onReconnect}
            style={{
              padding: '10px 20px',
              background: 'white',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="swap-horizontal-outline" style={{ fontSize: 16 }} />
            Reconnect Different Account
          </button>
          
          <button
            onClick={onComplete}
            style={{
              padding: '10px 20px',
              background: C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Continue to Step 3
            <Icon name="arrow-forward" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualACHForm({ client, onBack, onComplete }) {
  const [formData, setFormData] = useState({
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
    accountType: 'checking',
    accountHolderName: client?.company?.legalName || '',
    authorization: false,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  const validateRoutingNumber = (routing) => {
    if (!/^\d{9}$/.test(routing)) return false;
    
    // Routing number checksum validation
    const digits = routing.split('').map(Number);
    const checksum = (
      digits[0] * 3 + digits[1] * 7 + digits[2] * 1 +
      digits[3] * 3 + digits[4] * 7 + digits[5] * 1 +
      digits[6] * 3 + digits[7] * 7 + digits[8] * 1
    ) % 10;
    
    return checksum === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.bankName.trim()) newErrors.bankName = 'Bank name is required';
    if (!validateRoutingNumber(formData.routingNumber)) {
      newErrors.routingNumber = 'Invalid routing number (must be 9 digits with valid checksum)';
    }
    if (!/^\d{4,17}$/.test(formData.accountNumber)) {
      newErrors.accountNumber = 'Account number must be 4-17 digits';
    }
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Account numbers do not match';
    }
    if (!formData.authorization) {
      newErrors.authorization = 'You must authorize ACH transactions';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setSaving(true);
    try {
      await api.clients.setupManualACH(client.id, formData);
      window.location.reload();
    } catch (err) {
      console.error('Failed to save banking details:', err);
      setErrors({ general: 'Failed to save banking details. Please try again.' });
      setSaving(false);
    }
  };
  
  return (
    <div>
      <button
        onClick={onBack}
        style={{
          background: 'transparent',
          border: 'none',
          color: C.blue,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 16,
          padding: 0,
        }}
      >
        <Icon name="arrow-back" style={{ fontSize: 16 }} />
        Back to connection options
      </button>
      
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        Enter Banking Details Manually
      </h2>
      
      {/* Warning */}
      <div style={{
        background: C.orangeLight,
        border: `1px solid ${C.orange}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
        display: 'flex',
        alignItems: 'start',
        gap: 12
      }}>
        <Icon name="alert-circle" style={{ fontSize: 20, color: C.orange }} />
        <div style={{ fontSize: 13, color: C.text }}>
          Manual entry requires additional verification which may take 1-2 business days. For instant verification, use Plaid.
        </div>
      </div>
      
      {errors.general && (
        <div style={{
          background: C.redLight,
          border: `1px solid ${C.red}`,
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          fontSize: 13,
          color: C.red
        }}>
          {errors.general}
        </div>
      )}
      
      <form onSubmit={handleSubmit} style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32
      }}>
        <div style={{ display: 'grid', gap: 20 }}>
          {/* Bank Name */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Bank Name *
            </label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${errors.bankName ? C.red : C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
              placeholder="e.g., First National Bank"
            />
            {errors.bankName && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{errors.bankName}</div>
            )}
          </div>
          
          {/* Routing Number */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Routing Number * <span style={{ fontWeight: 400, color: C.textMid }}>(9 digits)</span>
            </label>
            <input
              type="text"
              value={formData.routingNumber}
              onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value.replace(/\D/g, '') })}
              maxLength={9}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${errors.routingNumber ? C.red : C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
              placeholder="123456789"
            />
            {errors.routingNumber && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{errors.routingNumber}</div>
            )}
          </div>
          
          {/* Account Number */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Account Number *
            </label>
            <input
              type="text"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
              maxLength={17}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${errors.accountNumber ? C.red : C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
              placeholder="Enter account number"
            />
            {errors.accountNumber && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{errors.accountNumber}</div>
            )}
          </div>
          
          {/* Confirm Account Number */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Confirm Account Number *
            </label>
            <input
              type="text"
              value={formData.confirmAccountNumber}
              onChange={(e) => setFormData({ ...formData, confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
              maxLength={17}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${errors.confirmAccountNumber ? C.red : C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
              placeholder="Re-enter account number"
            />
            {errors.confirmAccountNumber && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{errors.confirmAccountNumber}</div>
            )}
          </div>
          
          {/* Account Type */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Account Type *
            </label>
            <StyledSelect
              value={formData.accountType}
              onValueChange={(val) => setFormData({ ...formData, accountType: val })}
              options={[
                { value: "checking", label: "Checking" },
                { value: "savings", label: "Savings" }
              ]}
            />
          </div>
          
          {/* Account Holder Name */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Name on Account *
            </label>
            <input
              type="text"
              value={formData.accountHolderName}
              onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 14,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                boxSizing: 'border-box',
              }}
              placeholder="Account holder legal name"
            />
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 4 }}>
              (pre-filled from client legal name)
            </div>
          </div>
          
          {/* Authorization */}
          <div style={{
            background: C.bg,
            border: `1px solid ${errors.authorization ? C.red : C.border}`,
            borderRadius: 8,
            padding: 16
          }}>
            <label style={{ display: 'flex', alignItems: 'start', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.authorization}
                onChange={(e) => setFormData({ ...formData, authorization: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                I authorize HealthCues to initiate ACH debits and credits to this account for plan premium payments as outlined in the ACH Authorization Form.
              </span>
            </label>
            {errors.authorization && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 8, marginLeft: 28 }}>{errors.authorization}</div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              flex: 1,
              padding: '12px',
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              background: saving ? C.textLight : C.blue,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Icon name="save-outline" style={{ fontSize: 16 }} />
            {saving ? 'Saving...' : 'Save Banking Details'}
          </button>
        </div>
      </form>
    </div>
  );
}
